import { useBaseTopicRequestBuilder } from '../../utils/useBaseTopicRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import { TopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicRequestBuilder';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

export interface GetKeywordMetricsResponseRecord {
  searchTerm: string;
  type: string;
  brand: string;
  topic: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
}
export interface GetKeywordMetricsResponse {
  records: GetKeywordMetricsResponseRecord[];
}

/**
 * Fetches the total traffic by keyword data for topics
 */
export const useTopicTrafficByKeyword = ({
  sortField,
  includeClassification,
  topicType
}: {
  sortField: string;
  /**
   * When true, includes classification types in the response. Keywords can be
   * duplicated because a single keyword can have multiple features associated
   * with it
   */
  includeClassification: boolean;
  topicType?: string;
}) => {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const baseRequest = useBaseTopicRequestBuilder();

  const keywordsRequest = baseRequest
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .setSortField(sortField)
    .setTopicType(topicType ?? 'All')
    .build();

  const keywordsComparisonRequest = new TopicRequestBuilder(keywordsRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const route = includeClassification ? 'GetSearchTermAndBrandClassificationMetrics' : 'GetSearchTermMetrics';
  const { data, isLoading } = useQuery({
    queryKey: [TOPIC_QUERY_KEYS.all, `${sortField}KeywordsGrid`, route, keywordsRequest, keywordsComparisonRequest],
    queryFn: async () => {
      const endpoint = `/api/beacontopics/${route}`;
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetKeywordMetricsResponse>(endpoint, keywordsRequest),
        axios.post<GetKeywordMetricsResponse>(endpoint, keywordsComparisonRequest)
      ]);

      return [response, comparisonResponse];
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
  const response = data ?? [{ records: [] }, { records: [] }];

  return {
    response,
    isLoading
  };
};
